/**
 * Spreadsheets in: a menu, suppliers, ingredients, supplier prices, a team or
 * guests.
 *
 * A check tries every row for real and keeps nothing; an import does the same
 * and keeps the rows only if none failed. So the screen checks first, shows
 * what would go wrong, and offers the import only for a file that will go in
 * whole.
 *
 * On Professional, three more: a column layout can be saved and picked again
 * (a supplier whose sheet says "Unit Cost ex Tax"), an import can be taken
 * back out while nothing it added has been used, and a price sheet can be
 * fetched from an address on a clock.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';

import { useApi } from './provider';

export type ImportKindKey =
  'menu' | 'suppliers' | 'ingredients' | 'prices' | 'staff' | 'guests';

export interface ImportColumn {
  field: string;
  label: string;
  required: boolean;
  hint: string;
  /** Usually one value for the whole file, like a price sheet's supplier. */
  shared: boolean;
}

export interface ImportKind {
  key: ImportKindKey;
  label: string;
  plural: string;
  columns: ImportColumn[];
  /** Rows for a template to start from, in column order. */
  example: string[][];
  /** Whether this role can import this kind. */
  allowed: boolean;
  /** The plan that unlocks it, when this restaurant's does not. Null: open. */
  needs_plan: string | null;
  /** Whether it can be fetched on a schedule: it updates what is there. */
  repeatable: boolean;
}

export interface ImportRow {
  /** As the spreadsheet numbers it: the header is row 1. */
  row: number;
  ok: boolean;
  label: string;
  message: string | null;
  field: string | null;
  /** False for a row that passed and changed nothing (a price as it was). */
  changed: boolean;
}

export interface ImportMatch {
  field: string;
  label: string;
  required: boolean;
  /** The file's header feeding it, or null. */
  header: string | null;
  /** The value every row takes instead, when one was given. */
  fixed: string | null;
}

export interface ImportReport {
  kind: ImportKindKey;
  columns: ImportMatch[];
  /** Headers in the file that no column took. */
  ignored: string[];
  /** Every header in the file, in order, to match a column by hand. */
  headers: string[];
  rows: ImportRow[];
  total: number;
  failed: number;
  /** Rows that passed and changed nothing. */
  unchanged: number;
  /** True only when every row went in. */
  committed: boolean;
  /** The import to take back, when it went in and changed something. */
  run_id: string | null;
}

/** A column field -> the file's header for it, or the value every row takes. */
export type ImportFieldMap = Record<string, string>;

export interface RunImportInput {
  kind: ImportKindKey;
  csv: string;
  commit: boolean;
  /** Headers chosen by hand or from a saved layout. */
  columns?: ImportFieldMap;
  /** Values every row takes, for a column the file does not have. */
  fixed?: ImportFieldMap;
  fileName?: string;
}

export function useImportKinds() {
  const api = useApi();
  return useQuery({
    queryKey: ['imports', 'kinds'],
    queryFn: ({ signal }) => api.get<ImportKind[]>('/v1/imports', signal),
  });
}

export function useRunImport() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<ImportReport, ApiError, RunImportInput>({
    mutationFn: ({ kind, csv, commit, columns, fixed, fileName }) =>
      api.post<ImportReport>(`/v1/imports/${kind}`, {
        csv,
        commit,
        columns: columns ?? {},
        fixed: fixed ?? {},
        file_name: fileName ?? null,
      }),
    onSuccess: (report) => {
      // An import touches whichever lists its kind feeds; everything is
      // simpler to name than the four of them, and it happens once.
      if (report.committed) void queryClient.invalidateQueries();
    },
  });
}

/** A template a spreadsheet can open: the column names, and the example rows. */
export function importTemplate(kind: ImportKind): string {
  const cell = (value: string) =>
    /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  return [kind.columns.map((c) => c.label), ...kind.example]
    .map((row) => row.map(cell).join(','))
    .join('\r\n');
}

// --------------------------------------------------------------------------- //
// Saved column layouts (Professional: `imports.templates`)
// --------------------------------------------------------------------------- //

export interface ImportLayout {
  id: string;
  kind: ImportKindKey;
  name: string;
  columns: ImportFieldMap;
  fixed: ImportFieldMap;
}

/** The layouts saved for one kind, or every kind. 402 below Professional. */
export function useImportLayouts(kind: ImportKindKey | undefined, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['imports', 'layouts', kind ?? null],
    queryFn: ({ signal }) =>
      api.get<ImportLayout[]>(`/v1/imports/layouts${kind ? `?kind=${kind}` : ''}`, signal),
    enabled,
  });
}

/** Keep a layout under a name. The same name for the same kind replaces it. */
export function useSaveImportLayout() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    ImportLayout,
    ApiError,
    { kind: ImportKindKey; name: string; columns: ImportFieldMap; fixed: ImportFieldMap }
  >({
    mutationFn: (input) => api.post<ImportLayout>('/v1/imports/layouts', input),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['imports', 'layouts'] }),
  });
}

/** 409 while a scheduled import reads its file with it. */
export function useRemoveImportLayout() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { layoutId: string }>({
    mutationFn: ({ layoutId }) =>
      api.request<void>(`/v1/imports/layouts/${layoutId}`, { method: 'DELETE' }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['imports', 'layouts'] }),
  });
}

// --------------------------------------------------------------------------- //
// Past imports and undo (Professional: `imports.rollback`)
// --------------------------------------------------------------------------- //

export interface ImportRun {
  id: string;
  kind: ImportKindKey;
  /** A file somebody chose, or one fetched on a schedule. */
  source: 'file' | 'schedule';
  file_name: string | null;
  rows: number;
  created: number;
  updated: number;
  created_at: string;
  created_by: string | null;
  created_by_name: string | null;
  recurring_import_id: string | null;
  undone_at: string | null;
  /** Past this, or once undone, it cannot be taken back. */
  undo_until: string;
}

export interface ImportChange {
  entity: string;
  action: 'created' | 'updated';
  label: string;
}

/** Something an import added that stands in the way of undoing it. */
export interface ImportUndoBlocker {
  label: string;
  entity: string;
  reason: string;
}

/** The latest files that went in, newest first. On any plan. */
export function useImportRuns() {
  const api = useApi();
  return useQuery({
    queryKey: ['imports', 'runs'],
    queryFn: ({ signal }) => api.get<ImportRun[]>('/v1/imports/runs', signal),
  });
}

/** Everything one import added or changed: what undo would take back. */
export function useImportRunChanges(runId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['imports', 'runs', runId ?? null, 'changes'],
    queryFn: ({ signal }) =>
      api.get<ImportChange[]>(`/v1/imports/runs/${runId}/changes`, signal),
    enabled: Boolean(runId),
  });
}

/**
 * Take an import back out, whole. 409 with `blockers` in the error body when
 * anything it added has been used or edited since; nothing is taken back then.
 */
export function useUndoImport() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<ImportRun, ApiError, { runId: string }>({
    mutationFn: ({ runId }) => api.post<ImportRun>(`/v1/imports/runs/${runId}/undo`),
    // Undo removes rows from whichever lists the kind feeds.
    onSuccess: () => void queryClient.invalidateQueries(),
  });
}

/** The blockers on a refused undo, or none. */
export function undoBlockers(error: ApiError | null): ImportUndoBlocker[] {
  const blockers = (error?.body as { blockers?: unknown } | undefined)?.blockers;
  return Array.isArray(blockers) ? (blockers as ImportUndoBlocker[]) : [];
}

// --------------------------------------------------------------------------- //
// Scheduled imports (Professional: `imports.recurring`)
// --------------------------------------------------------------------------- //

export type RecurringOutcomeKind = 'applied' | 'unchanged' | 'refused' | 'failed';

export interface RecurringImportOutcome {
  id: string;
  ran_at: string;
  outcome: RecurringOutcomeKind;
  message: string;
  rows: number;
  failed: number;
  run_id: string | null;
  problems: { row: number; label: string; message: string }[];
}

export interface RecurringImport {
  id: string;
  name: string;
  kind: ImportKindKey;
  url: string;
  mapping_id: string | null;
  mapping_name: string | null;
  fixed: ImportFieldMap;
  frequency: 'daily' | 'weekly';
  hour: number;
  weekday: number | null;
  timezone: string;
  /** "Every Monday at 06:00". */
  description: string;
  is_active: boolean;
  next_run_at: string;
  last_run_at: string | null;
  last_outcome: RecurringOutcomeKind | null;
  last_message: string | null;
  /** The last few runs, newest first. */
  recent: RecurringImportOutcome[];
}

export interface RecurringImportInput {
  name: string;
  kind: ImportKindKey;
  url: string;
  mappingId: string | null;
  fixed: ImportFieldMap;
  frequency: 'daily' | 'weekly';
  hour: number;
  weekday: number | null;
}

/** Every import set to run on a clock. Readable on any plan. */
export function useRecurringImports(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['imports', 'recurring'],
    queryFn: ({ signal }) => api.get<RecurringImport[]>('/v1/imports/recurring', signal),
    enabled,
  });
}

function useRecurringMutation<TInput, TResult = unknown>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<TResult>,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['imports'] }),
  });
}

/** 402 below Professional; 409 with `field` for an address or kind it cannot use. */
export function useCreateRecurringImport() {
  return useRecurringMutation<RecurringImportInput>((api, input) =>
    api.post<RecurringImport>('/v1/imports/recurring', {
      name: input.name,
      kind: input.kind,
      url: input.url,
      mapping_id: input.mappingId,
      fixed: input.fixed,
      frequency: input.frequency,
      hour: input.hour,
      weekday: input.frequency === 'weekly' ? input.weekday : null,
    }),
  );
}

/** Pause or resume. Resuming starts from now; missed runs are not caught up. */
export function usePauseRecurringImport() {
  return useRecurringMutation<{ recurringId: string; active: boolean }>((api, input) =>
    api.request<RecurringImport>(`/v1/imports/recurring/${input.recurringId}`, {
      method: 'PATCH',
      body: { is_active: input.active },
    }),
  );
}

/** Stop it for good. Allowed on any plan. */
export function useRemoveRecurringImport() {
  return useRecurringMutation<{ recurringId: string }>((api, input) =>
    api.request<void>(`/v1/imports/recurring/${input.recurringId}`, { method: 'DELETE' }),
  );
}

/** Fetch and import it now, as the caller. Always answers with how it went. */
export function useRunRecurringImport() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<RecurringImportOutcome, ApiError, { recurringId: string }>({
    mutationFn: ({ recurringId }) =>
      api.post<RecurringImportOutcome>(`/v1/imports/recurring/${recurringId}/run`),
    // An applied run changes prices on whichever lists show them.
    onSuccess: () => void queryClient.invalidateQueries(),
  });
}
